import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getListDocumentsQueryKey } from "~/api/default/default";
import { useRestartDocumentPipeline } from "~/api/documents/documents";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";
import { PipelineMode, PipelineModeSelector } from "./PipelineModeSelector";

interface ReprocessCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  hasBookmarks: boolean;
  isPasswordProtected?: boolean;
}

export const ReprocessCaseDialog: FC<ReprocessCaseDialogProps> = ({
  open,
  setOpen,
  documentId,
  hasBookmarks,
  isPasswordProtected = false,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatMessage } = useIntl();
  const [mode, setMode] = useState<PipelineMode>(
    hasBookmarks ? "USE_TOC" : "USE_SNOWFLAKE"
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: restartDocumentPipeline, isPending: isLoading } =
    useRestartDocumentPipeline();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPending = isLoading || isSubmitting;

  const disabledModes = useMemo(() => {
    if (!hasBookmarks) {
      return [
        {
          mode: "USE_TOC" as PipelineMode,
          reason: formatMessage({
            id: "reprocessDocument.preserveStructure.noBookmarks",
            defaultMessage: "No bookmarks detected in source file.",
          }),
        },
      ];
    }
    return [];
  }, [hasBookmarks, formatMessage]);

  const handleSubmit = async () => {
    setError(null);

    if (isPasswordProtected && !password.trim()) {
      setError(
        formatMessage({
          id: "reprocessDocument.password.required",
          defaultMessage: "Please enter the document password.",
        })
      );
      return;
    }

    trackEvent({
      eventName: "app/case/actions_menu/reprocess.button/click",
      properties: { caseId, documentId, mode },
    });

    try {
      // USE_TOC means preserve original structure (skipTOC: false)
      // USE_SNOWFLAKE means smart auto-categorization (skipTOC: true)
      const skipTOC = mode === "USE_SNOWFLAKE";

      setIsSubmitting(true);
      if (isPasswordProtected) {
        await restartDocumentPipeline({
          organizationId,
          projectId: caseId,
          documentId,
          params: { skipTOC },
          data: { password: password.trim() },
        });
      } else {
        await restartDocumentPipeline({
          organizationId,
          projectId: caseId,
          documentId,
          params: { skipTOC },
          data: { password: undefined },
        });
      }

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      toast({
        title: formatMessage({ id: "success" }),
        description: formatMessage({
          id: "reprocessDocument.toast.success",
          defaultMessage: "Reprocessing started. Updates will appear shortly.",
        }),
      });

      setPassword("");
      setOpen(false);
    } catch {
      setError(
        formatMessage({
          id: isPasswordProtected
            ? "reprocessDocument.password.error"
            : "reprocessDocument.toast.error",
          defaultMessage: isPasswordProtected
            ? "Incorrect password or reprocessing failed. Please try again."
            : "Failed to start reprocessing. Please try again.",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeChange = (selectedMode: PipelineMode) => {
    setMode(selectedMode);
    trackEvent({
      eventName: "app/reprocess_modal/option.select",
      properties: {
        caseId,
        documentId,
        selectedMode,
        hasBookmarks,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="reprocessDocument.title"
              defaultMessage="Reprocess Document"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id={
                isPasswordProtected
                  ? "reprocessDocument.password.description"
                  : "reprocessDocument.description"
              }
              defaultMessage={
                isPasswordProtected
                  ? "This document is password-protected. Enter the password to unlock and reprocess it."
                  : "Choose how you want Sky AI to organize this document."
              }
            />
          </DialogDescription>
        </DialogHeader>

        {isPasswordProtected && (
          <div className="py-2">
            <Label
              htmlFor="document-password"
              className="mb-2 flex items-center gap-1.5"
            >
              <Lock className="h-3.5 w-3.5" />
              <FormattedMessage
                id="reprocessDocument.password.label"
                defaultMessage="Document Password"
              />
            </Label>
            <div className="relative">
              <Input
                id="document-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isPending) handleSubmit();
                }}
                placeholder={formatMessage({
                  id: "reprocessDocument.password.placeholder",
                  defaultMessage: "Enter PDF password",
                })}
                disabled={isPending}
                autoFocus
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
          </div>
        )}
        {!isPasswordProtected && (
          <div className="py-4">
            <PipelineModeSelector
              selectedMode={mode}
              onModeChange={handleModeChange}
              availableModes={["USE_TOC", "USE_SNOWFLAKE"]}
              disabledModes={disabledModes}
            />
          </div>
        )}
        {error && <div className="text-sm text-red-500 mb-2">{error}</div>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isPending}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || (isPasswordProtected && !password.trim())}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <FormattedMessage
                  id="reprocessDocument.button.confirming"
                  defaultMessage="Reprocessing..."
                />
              </>
            ) : (
              <FormattedMessage
                id="reprocessDocument.button.confirm"
                defaultMessage="Reprocess"
              />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
